import { TypoTooltip } from "@data-display/wrapper/DetectOverflow";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import WarningIcon from "@mui/icons-material/Warning";
import {
  type ChipProps,
  type ChipTypeMap,
  CircularProgress,
  ListSubheader,
  MenuItem,
  type MenuItemProps,
  type PopoverProps,
  Stack,
  type SvgIconProps,
  Tooltip,
  type TooltipProps,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import clsx from "clsx";
import type React from "react";
import { type MouseEvent, useCallback, useMemo, useState } from "react";
import { ChipButton, PopoverDropdown, chipButtonClasses, popoverDropdownClasses } from "./styles";

export interface IChipDropdownProps<
  OptionType extends { value: string; label: string; color?: ChipTypeMap["props"]["color"] },
> {
  value?: OptionType["value"];
  options: readonly OptionType[];
  variant?: ChipProps["variant"];
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onChange?: (value: OptionType) => void;
  chipSxProps?: SxProps<Theme>;
  disabled?: boolean;
  color?: ChipTypeMap["props"]["color"];
  warning?: boolean;
  defaultLabel?: string;
  alwaysDisplayArrow?: boolean;
  hideArrow?: boolean;
  tooltip?: string | React.ReactNode;
  loading?: boolean;
  ChipProps?: Partial<ChipProps>;
  PopoverProps?: Partial<PopoverProps>;
  IconProps?: Partial<SvgIconProps>;
  TooltipProps?: Partial<TooltipProps>;
  MenuItemProps?: Partial<MenuItemProps>;
  groupBy?: (option: OptionType) => string;
}

export const DropdownChip = <
  OptionType extends { value: string; label: string; color?: ChipTypeMap["props"]["color"]; hidden?: boolean },
>({
  value,
  options,
  variant,
  disabled,
  onClick,
  color,
  onChange,
  warning,
  defaultLabel = "Aucune valeur",
  alwaysDisplayArrow,
  hideArrow = false,
  tooltip,
  loading,
  IconProps,
  ChipProps,
  PopoverProps,
  TooltipProps,
  MenuItemProps,
  groupBy,
}: IChipDropdownProps<OptionType>) => {
  const [innerValue, setInnerValue] = useState<OptionType | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleChange = useCallback(
    (selectedOption: OptionType) => {
      setInnerValue(selectedOption);
      setAnchorEl(null);
      if (onChange) {
        onChange(selectedOption);
      }
    },
    [onChange]
  );
  const selectedValue = options.find((option) => value === option.value) ?? innerValue;
  const labelToDisplay = value
    ? (options.find((option) => value === option.value)?.label ?? value)
    : (innerValue?.label ?? defaultLabel);

  const displayedOptions = useMemo(() => options.filter((option) => !option.hidden), [options]);

  const groupedMenuItems = useMemo(() => {
    if (!groupBy) {
      return null;
    }
    return displayedOptions.reduce<{ group: string; options: OptionType[] }[]>((acc, option) => {
      const group = groupBy(option);
      const lastGroup = acc[acc.length - 1];
      if (lastGroup?.group === group) {
        lastGroup.options.push(option);
      } else {
        acc.push({ group, options: [option] });
      }
      return acc;
    }, []);
  }, [displayedOptions, groupBy]);

  const renderOptions = (groupBy: ((option: OptionType) => string) | undefined) => {
    if (groupBy) {
      return groupedMenuItems?.map(({ group, options: groupOptions }) => [
        group && (
          <ListSubheader key={group} sx={{ lineHeight: 2 }}>
            <Typography color="textPrimary" variant="body1SemiBold">
              {group}
            </Typography>
          </ListSubheader>
        ),
        ...groupOptions.map((option, index) => (
          <MenuItem
            key={`${option.value}-${index}`}
            {...MenuItemProps}
            className={popoverDropdownClasses.option}
            value={option.value}
            sx={{ pl: group ? 3 : undefined }}
            onClick={(evt) => {
              MenuItemProps?.onClick?.(evt);
              handleChange(option);
            }}
          >
            {option.label}
          </MenuItem>
        )),
      ]);
    }
    return displayedOptions.map((option, index) => (
      <MenuItem
        key={`${option.value}-${index}`}
        {...MenuItemProps}
        className={popoverDropdownClasses.option}
        value={option.value}
        onClick={(evt) => {
          MenuItemProps?.onClick?.(evt);
          handleChange(option);
        }}
      >
        {option.label}
      </MenuItem>
    ));
  };

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <ChipButton
          color={selectedValue?.color ?? color ?? "primary"}
          className={chipButtonClasses.root}
          size="small"
          label={
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
              }}
            >
              {tooltip ? (
                <Typography
                  variant={onClick && disabled ? "body2Underlined" : "body2SemiBold"}
                  className={clsx(chipButtonClasses.label)}
                >
                  {labelToDisplay}
                </Typography>
              ) : (
                <TypoTooltip
                  variant={onClick && disabled ? "body2Underlined" : "body2SemiBold"}
                  className={clsx(chipButtonClasses.label)}
                  TooltipProps={TooltipProps}
                >
                  {labelToDisplay}
                </TypoTooltip>
              )}
              {loading && <CircularProgress color="primary" size={14} sx={{ ml: 0.5 }} />}
              {!hideArrow &&
                (!disabled || alwaysDisplayArrow) &&
                (anchorEl ? <ArrowDropUpIcon {...IconProps} /> : <ArrowDropDownIcon {...IconProps} />)}
              {disabled && warning && (
                <WarningIcon {...IconProps} sx={{ width: 16, height: 16, marginLeft: 0.5, ...IconProps?.sx }} />
              )}
            </Stack>
          }
          variant={variant}
          {...ChipProps}
          {...((!disabled || onClick) && {
            onClick: (event: MouseEvent<HTMLDivElement>) => {
              ChipProps?.onClick?.(event);
              !disabled ? setAnchorEl(event.currentTarget) : onClick?.(event);
            },
          })}
        />
      </Tooltip>
      <PopoverDropdown
        className={popoverDropdownClasses.root}
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        {...PopoverProps}
        onClose={(...args) => {
          PopoverProps?.onClose?.(...args);
          setAnchorEl(null);
        }}
      >
        <Stack sx={{ py: 2 }}>{renderOptions(groupBy)}</Stack>
      </PopoverDropdown>
    </>
  );
};
